import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { t } from "@/shared/lib/th-translations";
import type { PeopleContribution } from "@/shared/types/production";

const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

function formatTHB(n: number): string {
  return THB_FORMATTER.format(n);
}

function formatTime(ts: number): string {
  const d = new Date(ts + 7 * 3600_000);
  return d.toISOString().slice(11, 16);
}

interface Props {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  /** When null = total across window. Otherwise YYYY-MM-DD filter. */
  day: string | null;
  /** Already filtered by row (machine when machineSplit on). */
  rows: PeopleContribution[];
  /** Show machine name in header when row is split by machine. */
  machine: string | null;
}

export default function PeopleContributionsModal({
  open,
  onClose,
  employeeName,
  day,
  rows,
  machine,
}: Props) {
  const filtered = useMemo(
    () => (day === null ? rows : rows.filter((r) => r.day === day)),
    [rows, day],
  );
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => b.timestamp - a.timestamp),
    [filtered],
  );

  const totalQty = sorted.reduce((s, r) => s + r.qty, 0);
  const totalShots = sorted.reduce((s, r) => s + r.shots, 0);
  const totalValue = sorted.reduce((s, r) => s + r.value, 0);

  const title =
    day === null
      ? t("production.people.drill.titleTotal", { name: employeeName })
      : t("production.people.drill.titleDay", { day, name: employeeName });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {machine && (
            <DialogDescription>
              {t("production.people.drill.subtitleMachine", { machine })}
            </DialogDescription>
          )}
        </DialogHeader>

        {sorted.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {t("production.people.drill.empty")}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3 px-1 pb-3 text-xs">
              <SummaryCell label={t("production.people.drill.summary.rows")} value={sorted.length.toLocaleString()} />
              <SummaryCell label={t("production.people.drill.summary.totalQty")} value={totalQty.toLocaleString()} />
              <SummaryCell label={t("production.people.drill.summary.totalShots")} value={totalShots.toLocaleString()} />
              <SummaryCell label={t("production.people.drill.summary.totalValue")} value={formatTHB(totalValue)} />
            </div>
            <div className="flex-1 overflow-auto border rounded-md">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                      {t("production.people.drill.col.task")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                      {t("production.people.drill.col.stage")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                      {t("production.people.drill.col.machine")}
                    </th>
                    <th className="px-3 py-2 text-center font-medium whitespace-nowrap">
                      {t("production.people.drill.col.time")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.drill.col.qty")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.drill.col.stageTotal")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.drill.col.share")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.drill.col.invoice")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.drill.col.value")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, idx) => {
                    const share = r.stageTotalQty > 0 ? (r.qty / r.stageTotalQty) * 100 : 0;
                    return (
                      <tr key={`${r.taskNumber}-${r.timestamp}-${idx}`} className="border-b hover:bg-gray-50/60">
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                          <Link
                            to={`/production/log?task=${encodeURIComponent(r.taskNumber)}`}
                            className="text-blue-600 hover:underline"
                          >
                            {r.taskNumber}
                          </Link>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.processTitle}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                          <Link
                            to={`/production/machines/${encodeURIComponent(r.machine)}`}
                            className="text-blue-600 hover:underline"
                          >
                            {r.machine}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-center whitespace-nowrap font-mono text-xs text-muted-foreground">
                          {r.day} {formatTime(r.timestamp)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                          {r.qty.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-muted-foreground">
                          {r.stageTotalQty.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                          {share.toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-muted-foreground">
                          {formatTHB(r.invoiceAmount)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-medium">
                          {formatTHB(r.value)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-gray-50/60 px-3 py-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}
